import styled, { css } from "styled-components";
import PageHeader from "../PageHeader";
import image from "../../../../../assets/img/image3.jpg";
import { blueColor, goldColor } from "../../../../styled/Theme";
import {
  ThunderboltOutlined,
  MessageOutlined,
  FileProtectOutlined,
  SafetyOutlined,
  ApiOutlined,
  GlobalOutlined,
  LineChartOutlined,
  ArrowRightOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";

const featureCards = [
  {
    icon: <ThunderboltOutlined />,
    number: "Real-time",
    label: "Payment processing and verification delivered instantly",
    accent: blueColor,
    size: "tall", // spans 2 rows
  },
  {
    icon: <MessageOutlined />,
    number: "Multi-channel",
    label: "Communication via in-app, SMS, and email channels",
    accent: goldColor,
    size: "normal",
  },
  {
    icon: <FileProtectOutlined />,
    number: "Full Audit",
    label: "Permanent digital record for every transaction and message",
    accent: "#22c55e",
    size: "normal",
  },
  {
    icon: <SafetyOutlined />,
    number: "Role-Based",
    label: "Access and approval workflows for secure operations",
    accent: "#a855f7",
    size: "wide", // spans 2 cols
  },
];

const benefitCards = [
  {
    icon: <ApiOutlined />,
    title: "Unified Financial Layer",
    body: "Integrates financial operations, official communication, and regulatory compliance into a single trusted layer for every entity type.",
    stat: "100%",
    statLabel: "Compliance Ready",
  },
  {
    icon: <GlobalOutlined />,
    title: "Universal Access",
    body: "Whether individual, business, institution, or government — clarity, speed, and transparency across all service touchpoints.",
    stat: "50+",
    statLabel: "Supported Entities",
  },
  {
    icon: <LineChartOutlined />,
    title: "Lower Operational Costs",
    body: "Reduces errors, cuts costs, and ensures every transaction and message is secure and traceable as your organization scales.",
    stat: "40%",
    statLabel: "Cost Reduction",
  },
];

const trustPoints = [
  "Bank-grade 256-bit encryption",
  "99.99% platform uptime SLA",
  "ISO 27001 certified infrastructure",
  "GDPR & regulatory compliant",
];

export default function SolutionsOverview() {
  return (
    <>
      <PageHeader
        title="Payments, Communication & Control, Unified"
        description="OnePayX is a digital operating platform combining secure payments with trusted communication for regulated environments."
        backgroundImage={image}
      />

      <Wrapper>
        {/* ── Section header ── */}
        <SectionHeaderWrap className="container">
          <SectionHeader
            data-aos="fade-up"
            data-aos-delay="100"
            data-aos-duration="700"
          >
            <Eyebrow>
              <EyebrowDot />
              Our Platform
            </Eyebrow>
            <SectionTitle>
              One <GoldWord>Interoperable</GoldWord> Digital Ecosystem
            </SectionTitle>
            <SectionSubtitle>
              OnePayX goes beyond payments. It enables transactions, messaging,
              notifications, enforcement, and engagement at national scale.
            </SectionSubtitle>
          </SectionHeader>
        </SectionHeaderWrap>

        {/* ── Bento Grid ── */}
        <BentoOuter className="container">
          <BentoGrid
            data-aos="fade-up"
            data-aos-delay="100"
            data-aos-duration="800"
          >
            {/* Hero image tile */}
            <BentoImageTile
              data-aos="fade-right"
              data-aos-delay="150"
              data-aos-duration="800"
            >
              <BentoImage src={image} alt="OnePayX platform overview" />
              <BentoImageOverlay>
                <OverlayPill>
                  <PillDot />
                  Secure &amp; Scalable
                </OverlayPill>
                <OverlayStats>
                  <OverlayStat>
                    <OverlayStatNum>99.9%</OverlayStatNum>
                    <OverlayStatLabel>Uptime</OverlayStatLabel>
                  </OverlayStat>
                  <OverlayStatDivider />
                  <OverlayStat>
                    <OverlayStatNum>256-bit</OverlayStatNum>
                    <OverlayStatLabel>Encryption</OverlayStatLabel>
                  </OverlayStat>
                </OverlayStats>
              </BentoImageOverlay>
            </BentoImageTile>

            {/* Feature tiles */}
            {featureCards.map((item, i) => (
              <BentoFeatureTile
                key={i}
                $size={item.size}
                $accent={item.accent}
                data-aos="fade-up"
                data-aos-delay={200 + i * 80}
                data-aos-duration="600"
              >
                <BentoTileIcon $accent={item.accent}>{item.icon}</BentoTileIcon>
                <BentoTileNumber $accent={item.accent}>
                  {item.number}
                </BentoTileNumber>
                <BentoTileLabel>{item.label}</BentoTileLabel>
                <BentoTileAccentBar $accent={item.accent} />
              </BentoFeatureTile>
            ))}
          </BentoGrid>
        </BentoOuter>

        {/* ── Dark benefits band ── */}
        <DarkBand>
          <DarkBandInner className="container">
            <DarkBandHeader
              data-aos="fade-up"
              data-aos-delay="100"
              data-aos-duration="700"
            >
              <DarkEyebrow>Why OnePayX</DarkEyebrow>
              <DarkTitle>
                Built for <GoldWord>Scale.</GoldWord> Designed for{" "}
                <GoldWord>Trust.</GoldWord>
              </DarkTitle>
            </DarkBandHeader>

            <BenefitRow>
              {benefitCards.map((item, i) => (
                <BenefitTile
                  key={i}
                  data-aos="fade-up"
                  data-aos-delay={200 + i * 120}
                  data-aos-duration="700"
                >
                  <BenefitStat>{item.stat}</BenefitStat>
                  <BenefitStatLabel>{item.statLabel}</BenefitStatLabel>
                  <BenefitDividerLine />
                  <BenefitIconRow>
                    <BenefitIconCircle>{item.icon}</BenefitIconCircle>
                    <BenefitTileTitle>{item.title}</BenefitTileTitle>
                  </BenefitIconRow>
                  <BenefitTileBody>{item.body}</BenefitTileBody>
                  <BenefitLearnMore>
                    Learn more <ArrowRightOutlined />
                  </BenefitLearnMore>
                </BenefitTile>
              ))}
            </BenefitRow>

            {/* Trust strip */}
            <TrustStrip
              data-aos="fade-up"
              data-aos-delay="500"
              data-aos-duration="600"
            >
              {trustPoints.map((point, i) => (
                <TrustPoint key={i}>
                  <CheckCircleFilled style={{ color: goldColor }} />
                  {point}
                </TrustPoint>
              ))}
            </TrustStrip>
          </DarkBandInner>
        </DarkBand>
      </Wrapper>
    </>
  );
}

/* ─── Styles ──────────────────────────────────────────────── */

const Wrapper = styled.section`
  background: #ffffff;
  padding-bottom: 0;
`;

const SectionHeaderWrap = styled.div`
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 680px;
  margin: 0 auto 52px;
  padding-top: 64px;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 1.1px;
  text-transform: uppercase;
  color: ${blueColor};
  background: ${blueColor}0f;
  border: 1px solid ${blueColor}28;
  border-radius: 20px;
  padding: 4px 12px;
  margin-bottom: 14px;
`;

const EyebrowDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${goldColor};
  flex-shrink: 0;
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: 800;
  letter-spacing: -1px;
  color: #0f172a;
  margin: 0 0 14px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 26px;
  }
`;

const GoldWord = styled.span`
  color: ${goldColor};
`;

const SectionSubtitle = styled.p`
  font-size: 16px;
  color: #64748b;
  line-height: 1.7;
  margin: 0;
`;

/* ── Bento Grid ── */
const BentoOuter = styled.div`
  padding: 0 20px;
  padding-bottom: 72px;
`;

const BentoGrid = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr;
  grid-template-rows: 220px 220px;
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto;
  }

  @media (max-width: 580px) {
    grid-template-columns: 1fr;
  }
`;

const BentoImageTile = styled.div`
  grid-column: 1;
  grid-row: 1 / 3;
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);

  @media (max-width: 900px) {
    grid-column: 1 / 3;
    grid-row: auto;
    height: 260px;
  }

  @media (max-width: 580px) {
    grid-column: 1;
    height: 220px;
  }
`;

const BentoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.7s ease;

  ${BentoImageTile}:hover & {
    transform: scale(1.04);
  }
`;

const BentoImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    160deg,
    transparent 40%,
    rgba(10, 25, 60, 0.72) 100%
  );
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 20px;
  gap: 12px;
`;

const OverlayPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 5px 13px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  width: fit-content;
`;

const PillDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 0 2px #22c55e40;
`;

const OverlayStats = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const OverlayStat = styled.div`
  display: flex;
  flex-direction: column;
`;

const OverlayStatNum = styled.div`
  font-size: 22px;
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.5px;
`;

const OverlayStatLabel = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.65);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

const OverlayStatDivider = styled.div`
  width: 1px;
  height: 32px;
  background: rgba(255, 255, 255, 0.25);
`;

/* Feature tiles */
const BentoFeatureTile = styled.div<{ $size?: string; $accent: string }>`
  background: #ffffff;
  border: 1px solid #e8f0f8;
  border-radius: 18px;
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  transition: all 0.28s ease;
  box-shadow: 0 2px 12px rgba(26, 111, 181, 0.05);
  cursor: default;

  /* wide tile spans 2 columns */
  ${(p) =>
    p.$size === "wide" &&
    css`
      grid-column: span 2;

      @media (max-width: 900px) {
        grid-column: span 1;
      }
    `}

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 14px 36px ${(p) => p.$accent}22;
    border-color: ${(p) => p.$accent}45;
  }
`;

const BentoTileAccentBar = styled.div<{ $accent: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 3px;
  height: 100%;
  background: ${(p) => p.$accent};
  border-radius: 18px 0 0 18px;
  opacity: 0.7;
`;

const BentoTileIcon = styled.div<{ $accent: string }>`
  width: 42px;
  height: 42px;
  border-radius: 11px;
  background: ${(p) => p.$accent}15;
  color: ${(p) => p.$accent};
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  transition: background 0.25s;

  ${BentoFeatureTile}:hover & {
    background: ${(p) => p.$accent}28;
  }
`;

const BentoTileNumber = styled.div<{ $accent: string }>`
  font-size: 18px;
  font-weight: 800;
  color: ${(p) => p.$accent};
  letter-spacing: -0.5px;
  margin-bottom: 6px;
`;

const BentoTileLabel = styled.p`
  font-size: 13px;
  color: #64748b;
  line-height: 1.55;
  margin: 0;
`;

/* ── Dark Benefits Band ── */
const DarkBand = styled.div`
  background: linear-gradient(135deg, #07142e 0%, #0d2454 55%, #091a3d 100%);
  padding: 72px 0 64px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -120px;
    right: -120px;
    width: 480px;
    height: 480px;
    border-radius: 50%;
    background: radial-gradient(circle, ${blueColor}25 0%, transparent 70%);
    pointer-events: none;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -80px;
    left: -80px;
    width: 360px;
    height: 360px;
    border-radius: 50%;
    background: radial-gradient(circle, ${goldColor}15 0%, transparent 70%);
    pointer-events: none;
  }
`;

const DarkBandInner = styled.div`
  padding: 0 20px;
  position: relative;
  z-index: 1;
`;

const DarkBandHeader = styled.div`
  margin-bottom: 48px;
`;

const DarkEyebrow = styled.div`
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: ${goldColor};
  margin-bottom: 12px;
`;

const DarkTitle = styled.h2`
  font-size: 34px;
  font-weight: 800;
  color: #ffffff;
  letter-spacing: -0.8px;
  line-height: 1.2;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const BenefitRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitTile = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 28px 24px;
  transition: all 0.28s ease;
  cursor: default;

  &:hover {
    background: rgba(255, 255, 255, 0.09);
    border-color: ${goldColor}55;
    transform: translateY(-4px);
  }
`;

const BenefitStat = styled.div`
  font-size: 42px;
  font-weight: 900;
  color: ${goldColor};
  letter-spacing: -2px;
  line-height: 1;
  margin-bottom: 4px;
`;

const BenefitStatLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.45);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 16px;
`;

const BenefitDividerLine = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin-bottom: 16px;
`;

const BenefitIconRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const BenefitIconCircle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${blueColor}30;
  color: #93c5fd;
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const BenefitTileTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #f1f5f9;
`;

const BenefitTileBody = styled.div`
  font-size: 13.5px;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.65;
  margin-bottom: 18px;
`;

const BenefitLearnMore = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${goldColor};
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  width: fit-content;
  transition: gap 0.2s;

  &:hover {
    gap: 10px;
  }
`;

/* Trust strip */
const TrustStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 32px;
  margin-top: 48px;
  padding-top: 32px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const TrustPoint = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
`;
